import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdsClientDetail from "./AdsClientDetail";
import BillingModal from "./BillingModal";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * Ads Management Hub — internal admin shell.
 *
 * List of intakes on the left half, per-client full detail on the right.
 * "Generate creatives" queues 6 placeholder slots (Zuops wiring is Phase 2).
 * Deep-linkable via `#client-<id>` so Slack ops pings jump straight to a client.
 */
export default function AdsHub({ user, onLogout, onSwitchHub }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [kickoffOpen, setKickoffOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ads/clients`);
      setClients(res.data.clients || []);
      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load clients");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
    const hash = window.location.hash || "";
    const m = hash.match(/#client-([\w-]+)/);
    if (m) setActiveId(m[1]);
  }, [loadClients]);

  const onSelectClient = (id) => {
    setActiveId(id);
    if (id) window.history.replaceState({}, "", `${window.location.pathname}#client-${id}`);
    else window.history.replaceState({}, "", window.location.pathname);
  };

  const filtered = clients.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [c.business_name, c.business_email, c.first_name, c.last_name, c.website]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const activeClient = clients.find((c) => c.client_id === activeId);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: "#07070f" }} data-testid="ads-hub">
      <TopBar user={user} onLogout={onLogout} onSwitchHub={onSwitchHub} onOpenKickoff={() => setKickoffOpen(true)} onOpenBilling={() => setBillingOpen(true)} />
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left rail — client list */}
        <div style={styles.listRail} data-testid="ads-client-list">
          <div style={styles.listHeader}>
            <div>
              <div style={styles.eyebrow}>Intake pipeline</div>
              <div style={styles.listCount}>{filtered.length} of {clients.length}</div>
            </div>
            <button onClick={loadClients} style={styles.refreshBtn} data-testid="ads-refresh" title="Refresh">
              ↻
            </button>
          </div>
          <input
            type="text"
            placeholder="Search business, contact, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={styles.search}
            data-testid="ads-search"
          />
          <div style={styles.listScroll}>
            {loading && <ListEmpty msg="Loading intakes…" />}
            {error && !loading && <ListEmpty msg={error} error />}
            {!loading && !error && filtered.length === 0 && (
              <ListEmpty msg={query ? "No matches." : "No intakes yet. First form submission lands here."} />
            )}
            {filtered.map((c) => (
              <ClientRow
                key={c.client_id}
                client={c}
                active={c.client_id === activeId}
                onClick={() => onSelectClient(c.client_id)}
              />
            ))}
          </div>
        </div>

        {/* Right pane — detail or empty-state dashboard */}
        <div style={styles.detailPane} data-testid="ads-client-detail-pane">
          {activeClient ? (
            <AdsClientDetail
              clientId={activeClient.client_id}
              onSaved={loadClients}
              onClose={() => onSelectClient(null)}
            />
          ) : (
            <EmptyDashboard
              clients={clients}
              onSelectClient={onSelectClient}
              onOpenKickoff={() => setKickoffOpen(true)}
            />
          )}
        </div>
      </div>
      {kickoffOpen && <KickoffPrepModal onClose={() => setKickoffOpen(false)} />}
      {billingOpen && <BillingModal onClose={() => setBillingOpen(false)} />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
const LEADSIE_META_CONNECT_URL = "https://app.leadsie.com/connect/request/6a9a6726f538c12220f409f7";

function TopBar({ user, onLogout, onSwitchHub, onOpenKickoff, onOpenBilling }) {
  const [copied, setCopied] = useState(false);
  const [copiedMeta, setCopiedMeta] = useState(false);
  const intakeUrl = useMemo(() => {
    const origin = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    return `${origin.replace(/\/$/, "")}/start`;
  }, []);
  const copyIntakeLink = () => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard.writeText(intakeUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const copyMetaLink = () => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard.writeText(LEADSIE_META_CONNECT_URL).then(() => {
      setCopiedMeta(true);
      setTimeout(() => setCopiedMeta(false), 2000);
    });
  };
  return (
    <div style={styles.topBar}>
      <img src="/skyeai_head.png" alt="Skye AI" width={22} height={22}
        style={{ objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(244,114,182,0.4))" }} />
      <span style={styles.brand}>Skye AI · Ads Hub</span>
      <span style={styles.phasePill}>Phase 3 · Admin</span>
      <span style={{ flex: 1 }} />
      <button onClick={onOpenKickoff} data-testid="adshub-open-kickoff" style={styles.kickoffPill}>
        ✅ Kickoff Prep
      </button>
      <button onClick={onOpenBilling} data-testid="adshub-open-billing" style={styles.billingPill}>
        💳 Charge customer
      </button>
      <div style={styles.linkBox} data-testid="adshub-intake-link">
        <span style={styles.linkLabel}>Intake link</span>
        <span style={styles.linkUrl} title={intakeUrl}>{intakeUrl.replace(/^https?:\/\//, "")}</span>
        <button onClick={copyIntakeLink} data-testid="adshub-copy-intake" style={styles.copyBtn}>
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <div style={styles.linkBoxMeta} data-testid="adshub-meta-link">
        <span style={styles.linkLabelMeta}>
          <MetaGlyph /> Meta connect
        </span>
        <span style={styles.linkUrl} title={LEADSIE_META_CONNECT_URL}>leadsie.com/connect · Skye</span>
        <button onClick={copyMetaLink} data-testid="adshub-copy-meta" style={styles.copyBtn}>
          {copiedMeta ? "✓ Copied" : "Copy"}
        </button>
      </div>
      {user?.email && <span style={styles.userChip}>{user.email}</span>}
      {onSwitchHub && (
        <button onClick={onSwitchHub} data-testid="adshub-switch-hub" style={styles.pillBtn}>
          Switch hub
        </button>
      )}
      <button onClick={onLogout} data-testid="adshub-logout" style={styles.pillBtn}>
        Log out
      </button>
    </div>
  );
}

// Tiny inline Meta gradient infinity mark — matches MetaLogo used elsewhere.
function MetaGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 32 32" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="mg-topbar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0064E0" />
          <stop offset="50%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#F472B6" />
        </linearGradient>
      </defs>
      <path
        d="M8 22c-3.31 0-6-2.69-6-6s2.69-6 6-6c2.1 0 3.9 1.08 5 2.72C14.1 11.08 15.9 10 18 10c3.31 0 6 2.69 6 6s-2.69 6-6 6c-2.1 0-3.9-1.08-5-2.72C11.9 20.92 10.1 22 8 22z"
        fill="none" stroke="url(#mg-topbar)" strokeWidth="3" strokeLinecap="round"
      />
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════
// META CONNECT (Leadsie) — dashboard card
// ═════════════════════════════════════════════════════════════════════
function MetaConnectCard() {
  const [copied, setCopied] = useState(false);
  const url = LEADSIE_META_CONNECT_URL;
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}&bgcolor=0f0f17&color=ffffff&qzone=2&margin=0`;
  return (
    <div style={dash.card} data-testid="dashboard-meta-connect">
      <div style={dash.cardHead}>
        <div>
          <div style={{ ...dash.cardEyebrow, color: "rgba(180,140,255,0.85)" }}>
            <MetaGlyph /> &nbsp;Meta account connect
          </div>
          <h2 style={dash.cardTitle}>Give a client one link to hook Meta up</h2>
        </div>
      </div>
      <div style={dash.linkArea}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ ...dash.urlBox, borderColor: "rgba(124,58,237,0.35)" }}
            data-testid="dashboard-meta-url"
          >
            {url}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={copy}
              data-testid="dashboard-copy-meta"
              style={{
                ...dash.primary,
                background: "linear-gradient(135deg, #0064E0 0%, #7C3AED 55%, #F472B6 100%)",
              }}
            >
              {copied ? "✓ Copied" : "Copy link"}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid="dashboard-open-meta"
              style={dash.ghost}
            >
              ↗ Open
            </a>
          </div>
          <div style={dash.hint}>
            Text or email it — the client grants Skye AI access to their Meta
            Business Manager in about 90 seconds. Sent by every rep on every
            new intake.
          </div>
        </div>
        <div style={dash.qrBox}>
          <img
            src={qrSrc}
            alt="QR code for Meta connect"
            style={dash.qrImg}
            data-testid="dashboard-meta-qr"
          />
          <div style={dash.qrLabel}>Scan on mobile</div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// EMPTY-STATE DASHBOARD — takes over the right pane when no client picked.
// Shows the pipeline at a glance, the intake link (with QR), a global
// Kickoff Prep button, and a fresh activity feed.
// ═════════════════════════════════════════════════════════════════════
function EmptyDashboard({ clients, onSelectClient, onOpenKickoff }) {
  const intakeUrl = useMemo(() => {
    const origin = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    return `${origin.replace(/\/$/, "")}/start`;
  }, []);

  const stats = useMemo(() => {
    const now = Date.now();
    const week = 7 * 24 * 3600 * 1000;
    const s = { total: clients.length, week: 0, drafting: 0, ready: 0 };
    for (const c of clients) {
      if (c.created_at) {
        try {
          if (now - new Date(c.created_at).getTime() < week) s.week++;
        } catch {}
      }
      if (c.status === "generating" || c.generation_status === "generating") s.drafting++;
      if (c.status === "final_ready" || c.status === "draft_ready") s.ready++;
    }
    return s;
  }, [clients]);

  const recent = useMemo(() => clients.slice(0, 5), [clients]);

  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(intakeUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(intakeUrl)}&bgcolor=0f0f17&color=ffffff&qzone=2&margin=0`;

  return (
    <div style={dash.wrap} data-testid="ads-empty-dashboard">
      {/* Hero */}
      <div style={dash.hero}>
        <div style={dash.eyebrow}>Ads pipeline overview</div>
        <h1 style={dash.h1}>Welcome back. Here's the pulse.</h1>
        <p style={dash.lead}>
          Pick a client on the left, or use this space to send new intake
          links, prep for your next kickoff call, and see who just landed.
        </p>
      </div>

      {/* Stats row */}
      <div style={dash.statsRow} data-testid="dashboard-stats">
        <StatCard label="Total intakes" value={stats.total} accent="#4ADEDE" />
        <StatCard label="This week" value={stats.week} accent="#B476FF" />
        <StatCard label="Drafting" value={stats.drafting} accent="#F472B6" />
        <StatCard label="Ready to review" value={stats.ready} accent="#4ADE80" />
      </div>

      {/* Main two-column */}
      <div style={dash.mainGrid}>
        {/* Left: Intake link + QR */}
        <div style={dash.card}>
          <div style={dash.cardHead}>
            <div>
              <div style={dash.cardEyebrow}>Send a new intake</div>
              <h2 style={dash.cardTitle}>Get a client onboarding in one link</h2>
            </div>
          </div>
          <div style={dash.linkArea}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={dash.urlBox} data-testid="dashboard-intake-url">
                {intakeUrl}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={copy} data-testid="dashboard-copy-link" style={dash.primary}>
                  {copied ? "✓ Copied" : "Copy link"}
                </button>
                <a
                  href={intakeUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="dashboard-open-link"
                  style={dash.ghost}
                >
                  ↗ Open
                </a>
              </div>
              <div style={dash.hint}>
                Text or email it to a client. Fresh submissions land in the
                pipeline on the left within seconds.
              </div>
            </div>
            <div style={dash.qrBox}>
              <img src={qrSrc} alt="QR code for intake form" style={dash.qrImg} data-testid="dashboard-qr" />
              <div style={dash.qrLabel}>Scan on mobile</div>
            </div>
          </div>
        </div>

        {/* Right: Kickoff prep */}
        <div style={dash.card}>
          <div style={dash.cardHead}>
            <div>
              <div style={{ ...dash.cardEyebrow, color: "#F472B6" }}>Kickoff prep</div>
              <h2 style={dash.cardTitle}>Never miss a question on the call</h2>
            </div>
          </div>
          <p style={{ ...dash.hint, marginTop: 4 }}>
            13 questions across Offer, Proof, Audience, and Positioning.
            Pull it up before every kickoff — click a client to track
            answers per business, or open the reference below.
          </p>
          <button
            onClick={onOpenKickoff}
            data-testid="dashboard-open-kickoff"
            style={{ ...dash.primary, marginTop: 18, alignSelf: "flex-start" }}
          >
            ✅ Open Kickoff Checklist
          </button>
        </div>

        {/* Middle: Meta connect (Leadsie) */}
        <MetaConnectCard />
      </div>

      {/* Recent activity */}
      {recent.length > 0 && (
        <div style={dash.card} data-testid="dashboard-recent">
          <div style={dash.cardHead}>
            <div>
              <div style={dash.cardEyebrow}>Recent submissions</div>
              <h2 style={dash.cardTitle}>Latest intakes to jump on</h2>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 14 }}>
            {recent.map((c) => (
              <button
                key={c.client_id}
                onClick={() => onSelectClient(c.client_id)}
                data-testid={`dashboard-recent-${c.client_id}`}
                style={dash.recentRow}
              >
                <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.business_name || "Unnamed business"}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                    {c.first_name} {c.last_name} · {c.monthly_budget ? `${c.monthly_budget}/mo ad spend` : "no ad budget yet"} · {c.ad_service_area || "no area"}
                  </div>
                </div>
                <span style={{ ...styles.statusPill, ...statusStyle(c.status) }}>{statusMap[c.status] || c.status || "new"}</span>
                <span style={{ color: "rgba(255,255,255,0.35)", fontFamily: "'Poppins', sans-serif", fontSize: 11, marginLeft: 12 }}>
                  {c.created_at ? relativeTime(new Date(c.created_at)) : "—"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ ...dash.statCard, borderColor: accent + "35" }} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div style={{ fontSize: 34, fontWeight: 800, color: accent, lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={dash.statLabel}>{label}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// KICKOFF PREP MODAL — global reference checklist accessible from top bar
// and empty-state. Read-only template so anyone can pull it up before a call.
// ═════════════════════════════════════════════════════════════════════
const CHECKLIST_TEMPLATE = [
  {
    title: "The Offer (highest leverage)",
    accent: "#F472B6",
    qs: [
      "If someone sees your ad, what's the single most compelling offer we can lead with?",
      "Do you already have an offer in mind, or do you want us to come up with one?",
      "Any price, guarantee, or urgency we can use? (e.g. $59 tune-up, free inspection, this-month-only, $0 down)",
      "Anything you legally can't say or don't want claimed?",
    ],
  },
  {
    title: "Proof & Credibility",
    accent: "#FBBF24",
    qs: [
      "What's your Google rating and roughly how many reviews?",
      "How long in business? Licensed / insured? Certifications or awards?",
      "Family-owned? Any guarantee or warranty?",
    ],
  },
  {
    title: "Who you want (audience)",
    accent: "#4ADE80",
    qs: [
      "Who's your ideal customer — homeowner vs renter, age range, income, situation?",
      "Which cities / neighborhoods or radius do you serve?",
      "Top 2-3 problems they come to you for?",
    ],
  },
  {
    title: "Services & positioning",
    accent: "#60A5FA",
    qs: [
      "Which services should we push hardest (most profitable / most wanted)?",
      "What makes you different from the other guys in your area?",
      "Emergency / same-day? Hours?",
    ],
  },
];

function KickoffPrepModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  const copyAll = () => {
    const txt = CHECKLIST_TEMPLATE.map((s) => `${s.title.toUpperCase()}\n${s.qs.map((q, i) => `  ${i + 1}. ${q}`).join("\n")}`).join("\n\n");
    navigator.clipboard?.writeText(txt);
  };
  return (
    <div style={modal.overlay} onClick={onClose} data-testid="kickoff-modal">
      <div style={modal.panel} onClick={(e) => e.stopPropagation()}>
        <div style={modal.head}>
          <div>
            <div style={{ ...dash.cardEyebrow, color: "#F472B6" }}>KICKOFF PREP · REFERENCE</div>
            <h2 style={{ ...dash.cardTitle, marginTop: 6 }}>13 questions to ask on every kickoff call</h2>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={copyAll} data-testid="kickoff-modal-copy-all" style={dash.ghost}>📋 Copy all</button>
            <button onClick={onClose} data-testid="kickoff-modal-close" style={dash.ghost}>✕ Close</button>
          </div>
        </div>
        <div style={modal.body}>
          {CHECKLIST_TEMPLATE.map((section, i) => (
            <div key={i} style={{ marginBottom: 22 }}>
              <div style={{ ...modal.sectionTitle, color: section.accent, borderColor: section.accent + "55" }}>
                {section.title}
              </div>
              <ol style={modal.qList}>
                {section.qs.map((q, j) => <li key={j} style={modal.qItem}>{q}</li>)}
              </ol>
            </div>
          ))}
          <div style={modal.foot}>
            💡 Tip: open a specific client on the left to track answers per business —
            checkboxes and notes auto-save to their brief.
          </div>
        </div>
      </div>
    </div>
  );
}

const dash = {
  wrap: {
    padding: "36px 40px 60px", fontFamily: "'Poppins', system-ui, sans-serif",
    color: "#fff", maxWidth: 1100, margin: "0 auto",
  },
  hero: { marginBottom: 32 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
    color: "rgba(74,222,222,0.75)", fontWeight: 700, marginBottom: 16,
  },
  h1: {
    fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em",
    color: "#fff", marginBottom: 10, lineHeight: 1.1,
  },
  lead: {
    fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,0.55)",
    maxWidth: 560,
  },
  statsRow: {
    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14,
    marginBottom: 24,
  },
  statCard: {
    padding: "18px 20px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid",
    borderRadius: 12,
    minHeight: 100,
    display: "flex", flexDirection: "column", justifyContent: "space-between",
  },
  statLabel: {
    fontSize: 11.5, fontWeight: 700, color: "rgba(255,255,255,0.55)",
    letterSpacing: 0.3, textTransform: "uppercase", marginTop: 8,
    fontFamily: "'Poppins', sans-serif",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20, marginBottom: 24,
  },
  card: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14, padding: 22,
    display: "flex", flexDirection: "column",
  },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  cardEyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(74,222,222,0.75)", fontWeight: 700, marginBottom: 8,
  },
  cardTitle: {
    fontSize: 18, fontWeight: 800, letterSpacing: "-0.015em", color: "#fff",
  },
  hint: {
    fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginTop: 8,
  },
  linkArea: {
    display: "flex", gap: 20, alignItems: "flex-start", marginTop: 16,
  },
  urlBox: {
    padding: "12px 14px",
    background: "#0A0A0F",
    border: "1px solid rgba(74,222,222,0.24)",
    borderRadius: 10,
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12.5, color: "rgba(255,255,255,0.85)",
    letterSpacing: 0.3, wordBreak: "break-all", lineHeight: 1.5,
  },
  primary: {
    padding: "10px 18px", borderRadius: 999,
    background: "linear-gradient(135deg, #4ADEDE, #B476FF)",
    color: "#0A0A0A", cursor: "pointer",
    fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2,
    border: "none", fontFamily: "'Poppins', sans-serif",
  },
  ghost: {
    padding: "10px 18px", borderRadius: 999,
    background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.75)",
    fontSize: 12.5, fontWeight: 700,
    border: "1px solid rgba(255,255,255,0.08)",
    cursor: "pointer", textDecoration: "none",
    fontFamily: "'Poppins', sans-serif",
    display: "inline-flex", alignItems: "center",
  },
  qrBox: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    flexShrink: 0,
  },
  qrImg: {
    width: 128, height: 128,
    background: "#0f0f17", padding: 6,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.06)",
  },
  qrLabel: {
    fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)", fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
  },
  recentRow: {
    display: "flex", alignItems: "center", gap: 12,
    width: "100%", padding: "12px 14px",
    background: "transparent", border: "1px solid transparent",
    borderRadius: 10, cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    transition: "all 0.15s ease",
  },
};

const modal = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 100,
    background: "rgba(0,0,0,0.7)",
    backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, animation: "fade-in 0.2s ease",
  },
  panel: {
    width: "min(720px, 100%)", maxHeight: "88vh",
    background: "#12121A",
    border: "1px solid rgba(244,114,182,0.28)",
    borderRadius: 16, overflow: "hidden",
    display: "flex", flexDirection: "column",
    boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
  },
  head: {
    padding: "20px 24px",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  body: { padding: "16px 24px 24px", overflowY: "auto" },
  sectionTitle: {
    fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
    fontFamily: "'Poppins', sans-serif",
    paddingBottom: 8, marginBottom: 12, borderBottom: "1px solid",
  },
  qList: { paddingLeft: 20, margin: 0, display: "flex", flexDirection: "column", gap: 8 },
  qItem: {
    fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,0.85)",
    fontFamily: "'Poppins', sans-serif",
  },
  foot: {
    marginTop: 24,
    padding: "12px 14px",
    background: "rgba(74,222,222,0.05)",
    border: "1px solid rgba(74,222,222,0.24)",
    borderRadius: 10,
    fontSize: 12.5, color: "rgba(255,255,255,0.7)",
    fontFamily: "'Poppins', sans-serif",
  },
};

// ═════════════════════════════════════════════════════════════════════
function ClientRow({ client, active, onClick }) {
  const created = client.created_at ? new Date(client.created_at) : null;
  const timeAgo = created ? relativeTime(created) : "—";
  const statusLabel = statusMap[client.status] || client.status || "new";
  return (
    <button
      onClick={onClick}
      data-testid={`ads-client-row-${client.client_id}`}
      style={{ ...styles.row, ...(active ? styles.rowActive : {}) }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ ...styles.rowName, color: active ? "#fff" : "rgba(255,255,255,0.92)" }}>
          {client.business_name || "Unnamed business"}
        </div>
        <div style={styles.rowMeta}>
          {client.first_name} {client.last_name} · {client.monthly_budget ? `${client.monthly_budget}/mo ad spend` : "no ad budget"}
        </div>
      </div>
      <div style={styles.rowRight}>
        <div style={{ ...styles.statusPill, ...statusStyle(client.status) }}>{statusLabel}</div>
        <div style={styles.rowTime}>{timeAgo}</div>
      </div>
    </button>
  );
}

function ListEmpty({ msg, error }) {
  return (
    <div style={{ padding: 20, textAlign: "center", color: error ? "#f87171" : "rgba(255,255,255,0.4)", fontSize: 13 }}>
      {msg}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────
const statusMap = {
  new: "New",
  generating: "Drafting",
  draft_ready: "Drafts ready",
  call_attached: "Kickoff done",
  final_ready: "Finals ready",
};
function statusStyle(s) {
  const c = {
    new: ["#6b6b66", "rgba(107,107,102,0.14)"],
    generating: ["#F472B6", "rgba(244,114,182,0.12)"],
    draft_ready: ["#60A5FA", "rgba(96,165,250,0.12)"],
    call_attached: ["#FBBF24", "rgba(251,191,36,0.12)"],
    final_ready: ["#4ADE80", "rgba(74,222,128,0.12)"],
  }[s] || ["#6b6b66", "rgba(107,107,102,0.14)"];
  return { color: c[0], background: c[1], borderColor: c[0] + "55" };
}
function relativeTime(d) {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── styles ───────────────────────────────────────────────────────
const styles = {
  topBar: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 18px",
    background: "#050508",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    flexShrink: 0,
  },
  brand: {
    fontFamily: "'Poppins', system-ui, sans-serif",
    fontWeight: 800, fontSize: 14, letterSpacing: "-0.01em", color: "#fff",
  },
  phasePill: {
    fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(244,114,182,0.75)", fontFamily: "'Poppins', sans-serif",
    padding: "3px 8px",
    border: "1px solid rgba(244,114,182,0.28)",
    borderRadius: 999, background: "rgba(244,114,182,0.06)",
  },
  kickoffPill: {
    padding: "6px 14px", borderRadius: 999,
    background: "linear-gradient(135deg, rgba(244,114,182,0.12), rgba(180,118,255,0.12))",
    border: "1px solid rgba(244,114,182,0.32)",
    color: "#F472B6", cursor: "pointer",
    fontSize: 11.5, fontWeight: 800, letterSpacing: 0.2,
    fontFamily: "'Poppins', system-ui, sans-serif",
  },
  billingPill: {
    padding: "6px 14px", borderRadius: 999, marginLeft: 6,
    background: "linear-gradient(135deg, rgba(74,222,128,0.14), rgba(74,222,222,0.10))",
    border: "1px solid rgba(74,222,128,0.35)",
    color: "#4ADE80", cursor: "pointer",
    fontSize: 11.5, fontWeight: 800, letterSpacing: 0.2,
    fontFamily: "'Poppins', system-ui, sans-serif",
  },
  linkBox: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "5px 6px 5px 12px",
    background: "rgba(74,222,222,0.06)",
    border: "1px solid rgba(74,222,222,0.24)",
    borderRadius: 999,
    fontFamily: "'Poppins', system-ui, sans-serif",
    maxWidth: 420,
  },
  linkLabel: {
    fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(74,222,222,0.85)", flexShrink: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  linkBoxMeta: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "5px 6px 5px 12px",
    background: "rgba(124,58,237,0.08)",
    border: "1px solid rgba(124,58,237,0.32)",
    borderRadius: 999,
    fontFamily: "'Poppins', system-ui, sans-serif",
    maxWidth: 380,
  },
  linkLabelMeta: {
    display: "inline-flex", alignItems: "center", gap: 5,
    fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(180,140,255,0.95)", flexShrink: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  linkUrl: {
    fontSize: 11.5, color: "rgba(255,255,255,0.85)",
    fontFamily: "'Poppins', sans-serif",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    minWidth: 0, flex: "1 1 auto",
  },
  copyBtn: {
    padding: "4px 12px", borderRadius: 999,
    background: "linear-gradient(135deg, #4ADEDE, #B476FF)",
    color: "#0A0A0A", cursor: "pointer",
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
    border: "none",
    fontFamily: "'Poppins', system-ui, sans-serif",
    flexShrink: 0,
  },
  userChip: {
    fontSize: 11, color: "rgba(255,255,255,0.55)",
    fontFamily: "'Poppins', sans-serif", letterSpacing: 0.4,
  },
  pillBtn: {
    padding: "6px 14px", borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.03)",
    color: "rgba(255,255,255,0.7)",
    fontSize: 11, fontWeight: 700, cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
  },
  listRail: {
    width: 380, minWidth: 320, maxWidth: 420,
    background: "#0A0A0F",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    display: "flex", flexDirection: "column", flexShrink: 0,
  },
  listHeader: {
    padding: "18px 20px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  listCount: {
    marginTop: 6, fontSize: 14, fontWeight: 800, color: "rgba(255,255,255,0.85)",
    fontFamily: "'Poppins', sans-serif",
  },
  refreshBtn: {
    width: 30, height: 30, borderRadius: 8,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.6)", cursor: "pointer",
    fontSize: 16, fontFamily: "'Poppins', sans-serif",
  },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10, letterSpacing: 1.6,
    textTransform: "uppercase", color: "rgba(244,114,182,0.65)", fontWeight: 700,
  },
  search: {
    margin: "0 20px 12px",
    padding: "9px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8, color: "#fff",
    fontSize: 13, fontFamily: "'Poppins', sans-serif",
    outline: "none",
  },
  listScroll: { flex: 1, overflow: "auto", padding: "0 12px 20px" },
  row: {
    width: "100%", textAlign: "left",
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 12px", marginBottom: 4,
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 10, cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    transition: "all 0.15s ease",
  },
  rowActive: {
    background: "rgba(244,114,182,0.06)",
    border: "1px solid rgba(244,114,182,0.28)",
  },
  rowName: { fontSize: 13.5, fontWeight: 700, letterSpacing: "-0.005em" },
  rowMeta: {
    fontSize: 11.5, color: "rgba(255,255,255,0.42)",
    marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  rowRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 },
  statusPill: {
    padding: "3px 8px", borderRadius: 999,
    fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    border: "1px solid", fontFamily: "'Poppins', sans-serif",
  },
  rowTime: {
    fontSize: 10, color: "rgba(255,255,255,0.32)",
    fontFamily: "'Poppins', sans-serif",
  },
  detailPane: { flex: 1, overflow: "auto", background: "#0F0F17" },
  emptyWrap: {
    height: "100%", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center",
    fontFamily: "'Poppins', sans-serif",
  },
  emptyEyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
    color: "rgba(244,114,182,0.55)", fontWeight: 700, marginBottom: 16,
  },
  emptyH2: {
    fontSize: 26, fontWeight: 800, color: "#fff",
    letterSpacing: "-0.02em", marginBottom: 12, maxWidth: 520,
  },
  emptyLead: {
    fontSize: 14, color: "rgba(255,255,255,0.5)", lineHeight: 1.6, maxWidth: 480,
  },
};

// Inject animations + dashboard hover states + responsive grid
if (typeof document !== "undefined" && !document.getElementById("adshub-anim")) {
  const st = document.createElement("style");
  st.id = "adshub-anim";
  st.textContent = `
    @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
    button[data-testid^="dashboard-recent-"]:hover {
      background: rgba(180,118,255,0.06) !important;
      border-color: rgba(180,118,255,0.22) !important;
      transform: translateX(2px);
    }
    button[data-testid="dashboard-copy-link"]:hover,
    button[data-testid="dashboard-open-kickoff"]:hover,
    button[data-testid="adshub-open-kickoff"]:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 32px rgba(180,118,255,0.28);
    }
    a[data-testid="dashboard-open-link"]:hover,
    button[data-testid="kickoff-modal-close"]:hover,
    button[data-testid="kickoff-modal-copy-all"]:hover {
      background: rgba(255,255,255,0.08) !important;
      color: #fff !important;
    }
    @media (max-width: 1080px) {
      [data-testid="dashboard-stats"] { grid-template-columns: repeat(2, 1fr) !important; }
    }
    @media (max-width: 820px) {
      [data-testid="ads-empty-dashboard"] > div:nth-child(3) {
        grid-template-columns: 1fr !important;
      }
    }
  `;
  document.head.appendChild(st);
}
